import { themes } from 'kb-shared';
import { History } from 'history';
import React from 'react';
import gql from 'graphql-tag';
import m from 'moment';
import { useQuery } from 'react-apollo';
import styled from 'styled-components';

import {
  profile,
  paperplane,
  mailing,
  pharmacy,
  gender,
  phone,
  birthday
} from '../../assets/icons';

export const patientQuery = gql`
  query profileWidget {
    patient {
      id
      gender
      birthday
      email
      phone
      address {
        id
        address1
        address2
        city
        state
        zipcode
      }
      patientPharmacy {
        id
        name
        phone
      }
      patientTrustees {
        id
        name
        phone
        state
        relationship
      }
    }
  }
`;

interface PatientResponse {
  patient: {
    id: string;
    gender: string;
    birthday: string;
    email: string;
    phone: string;
    address?: {
      address1?: string;
      address2?: string;
      city?: string;
      state?: string;
      zipcode?: string;
    };
    patientPharmacy?: {
      id: string;
      name?: string;
      phone?: string;
    };
  };
}

type Props = {
  history: History;
};

const formatPhoneNumber = (phone?: string) => {
  const cleaned = ('' + phone).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return phone;
};

const Row = (icon: string, altText: string, detail?: string, capitalize?: boolean) => (
  <DetailRow>
    <Icon src={icon} alt={`${altText}-icon`} />
    <Text className={`detail${capitalize && '-capitalize'}`}>{detail}</Text>
  </DetailRow>
);

const ProfileWidget = ({ history }: Props) => {
  const { data, error } = useQuery<PatientResponse>(patientQuery);

  const patient = data?.patient;

  if (!patient || error) {
    return null;
  }

  const birthdate = patient?.birthday ? m(patient.birthday).format('MMMM DD, YYYY') : undefined;
  const patientPhone = formatPhoneNumber(patient.phone);
  const pharmacyPhone = formatPhoneNumber(patient.patientPharmacy?.phone);
  const { address, patientPharmacy } = patient;

  return (
    <Container>
      <ProfileIcon src={profile} alt={'icon'} />
      <Text className="heading">My Profile</Text>
      <DetailsWrapper>
        <DetailsContainer>
          {Row(gender, 'gender', patient.gender, true)}
          {Row(birthday, 'birthday', birthdate)}
        </DetailsContainer>
        <DetailsContainer>
          <DetailRow>
            <Icon src={pharmacy} alt="pharmacy-icon" />
            <CompoundDetail>
              <Text className="detail">{patientPharmacy?.name}</Text>
              <br />
              <Text className="detail">{pharmacyPhone}</Text>
            </CompoundDetail>
          </DetailRow>
        </DetailsContainer>
        <DetailsContainer>
          {Row(paperplane, 'email', patient?.email)}
          {Row(phone, 'phone', patientPhone)}
          <DetailRow>
            <Icon src={mailing} alt="mailing-address-icon" />
            <CompoundDetail>
              {address?.address1 && <Text className="detail">{address?.address1}</Text>}
              {address?.address2 && <Text className="detail">{address?.address2}</Text>}
              <Text className="detail">
                {address?.address1 && address?.city + ','} {address?.state && address?.state}{' '}
                {address?.zipcode && address?.zipcode}
              </Text>
            </CompoundDetail>
          </DetailRow>
        </DetailsContainer>
      </DetailsWrapper>
      <UpdateButton onClick={() => history.push('/my-profile')}>Update Your Details</UpdateButton>
    </Container>
  );
};

export default ProfileWidget;

const UpdateButton = styled.button`
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  width: 205px;
  height: 48px;
  border-radius: 24px;
  border: solid 1px ${themes.colors.KBNight};
  font-size: 14px;
  text-transform: uppercase;
  outline: none;

  :hover {
    color: white;
    background-color: ${themes.colors.KBNight};
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  border: solid 4px ${themes.colors.KBNight};
  background-color: ${themes.colors.KBWhite};
  padding: 20px 25px 20px 25px;
  min-height: 100%;
`;

const ProfileIcon = styled.img`
  width: 81px;
  height: 75px;
  object-fit: contain;
  margin-bottom: 12px;
`;

const Text = styled.div`
  color: ${themes.colors.KBNight};
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  font-size: 16px;
  width: 100%;
  overflow-wrap: anywhere;
  &.heading {
    ${({ theme }) => theme.fonts.DomaineDisplay};
    font-size: 24px;
    text-align: center;
  }
  &.detail {
    ${({ theme }) => theme.fonts.FoundersGrotesk};
    font-size: 16px;
    color: ${themes.colors.KBNight};
    &-capitalize {
      text-transform: capitalize;
    }
  }
`;

const CompoundDetail = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
`;

const DetailsContainer = styled.div`
  background-color: ${themes.colors.beige};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  width: 100%;
  ${DetailRow}:last-child {
    margin-bottom: 0;
  }
`;

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 20px;
  width: 100%;
  ${DetailsContainer} {
    margin-bottom: 1px;
  }
`;

const Icon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  margin-right: 20px;
`;
